import { useGet, usePatch } from "@albertine/api-client";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import type { CatalogItemFull } from "@albertine/types/openapi";
import classNames from "classnames";
import { useState } from "react";
import { useHistory } from "react-router";
import type { Agent } from "../../common/types/Agent";
import {
    type Booking,
    type BookingInMessage,
    parseBookingToMessage,
} from "../../common/types/Booking";
import type { Conversation } from "../../common/types/Conversation";
import { InternalNote, type MessageOrInternalNote } from "../../common/types/Message";
import type { Request } from "../../common/types/Request";
import { isTypeOf } from "../../common/utils/zod.util";
import { mapApiBookingToBooking } from "../../utils/booking.adapter";
import routes from "../../utils/routes";
import BookingThumbnail from "../Bookings/BookingThumbnail";
import { Flex } from "../Common/Flex";
import {
    addToBookingArray,
    type MessageInputContent,
    type MessageState,
    type MessageType,
} from "./ChatContext";
import "./EditMessage.css";
import MessageInput from "./MessageInput";

type Props = {
    request: Request | undefined;
    conversation: Conversation;
    currentAgent: Agent;
    member: MemberDataFirestoreRecord;
    message: MessageOrInternalNote;
    close: (action?: "cancel") => void;
};

function EditMessage(props: Props) {
    const { conversation, currentAgent, member, request, message, close } = props;
    const { mutateAsync: editConversationMessage } = usePatch(
        "editConversationMessage",
    );
    const [replyText, setReplyText] = useState<string | undefined>(message.message);
    const [replyHtml, setReplyHtml] = useState<string | undefined>(
        message.messageHtml || message.message,
    );
    const [bookings, setBookings] = useState<BookingInMessage[]>(
        message.bookingSuggestions || [],
    );

    const history = useHistory();
    const { mutateAsync: editInternalNote } = usePatch("editInternalNote");

    const { data: bookingsData } = useGet(
        "bookings",
        { query: { requestId: message.request ?? "" } },
        { enabled: !!message.request },
    );

    const requestBookings = bookingsData?.bookings.map(mapApiBookingToBooking);

    const messageType: MessageType = isTypeOf(message, InternalNote)
        ? "internal_note"
        : "message";

    const addBooking: (b: Booking) => void = (booking: Booking) => {
        const suggestion = parseBookingToMessage(booking);
        if (bookings) {
            setBookings(addToBookingArray(booking, bookings as Booking[]));
        } else {
            setBookings([suggestion]);
        }
    };

    const removeBooking = (b: BookingInMessage) => {
        if (bookings) {
            setBookings([...bookings.filter((booking) => booking.id !== b.id)]);
        }
    };

    const messageState: MessageState = {
        contextKey: undefined,
        messageType,
        setMessageType: () => {},
        replyText,
        setReplyText,
        replyHtml,
        setReplyHtml,
        bookings: bookings as (Booking | BookingInMessage)[],
        addBooking,
        removeBooking,
        attachments: message.attachments!,
        addFilesToAttachments: () => {},
        removeAttachment: undefined,
        clearChatContext: () => {},
        articles: message.articles!,
        articlesV2: message.articlesV2 as CatalogItemFull[],
        addArticle: () => {},
        removeArticle: undefined,
        addArticleV2: () => {},
        removeArticleV2: undefined,
    };

    const saveEdited = async (content: MessageInputContent) => {
        if (messageType === "message") {
            await editConversationMessage({
                param: {
                    conversationId: conversation.id,
                    messageId: message.id,
                },
                json: {
                    message: replyText || "",
                    messageHtml: replyHtml && replyHtml.length > 0 ? replyHtml : null,
                    bookingIds:
                        bookings.length !== 0 ? bookings.map((b) => b.id) : null,
                },
            });
        } else {
            const mentions = content.mentionedAgents.map((it) => it.id);

            await editInternalNote({
                param: {
                    conversationId: conversation.id,
                    noteId: message.id,
                },
                json: {
                    note: {
                        message: replyText || "",
                        messageHtml:
                            replyHtml && replyHtml.length > 0 ? replyHtml : null,
                        ...(message.attachments
                            ? { attachments: message.attachments }
                            : {}),
                    },
                    mentions,
                    ...(mentions.length > 0 && content.dueDate
                        ? { mentionDueDate: content.dueDate.toISOString() }
                        : {}),
                },
            });
        }
        close();
    };

    const isMessageWithBookings =
        messageType !== "internal_note" &&
        requestBookings &&
        requestBookings.length > 0;
    return (
        <div
            className={classNames(
                "edit-message",
                isMessageWithBookings && "edit-message--with-bookings",
            )}
            onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                close("cancel");
            }}
        >
            <Flex.Row
                className={classNames(
                    "edit-message__content",
                    isMessageWithBookings && "edit-message__content--with-bookings",
                )}
                alignItems="stretch"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                {isMessageWithBookings && (
                    <Flex.Column gap="large" className="edit-message__request-bookings">
                        <h4>Bookings for {message.requestTitle}</h4>

                        <Flex.Column gap="medium">
                            {requestBookings.map((b) => (
                                <BookingThumbnail
                                    key={b.id}
                                    booking={b}
                                    onAdd={() => {
                                        addBooking(b);
                                    }}
                                    onClick={() => {
                                        history.push(
                                            routes.requestBooking(b.request, b.id),
                                        );
                                    }}
                                    hideProposalFeedback
                                />
                            ))}
                        </Flex.Column>
                    </Flex.Column>
                )}
                <MessageInput
                    action="edit"
                    editedMessageTimestamp={message.sentAt}
                    messageBeingEdited={message}
                    request={request}
                    currentAgent={currentAgent}
                    member={member}
                    messageState={messageState}
                    sendAction={async (content) => {
                        if (content.replyText !== "") {
                            await saveEdited(content);
                        } else {
                            window.alert("Cannot remove message content.");
                        }
                    }}
                    closeAction={close}
                />
            </Flex.Row>
        </div>
    );
}

export default EditMessage;
